import get from 'lodash/get';
import { STPlaceholderType } from '@tencent/slide-sdk-pb-model';
import { Frame } from '../utils/transform-utils';
import { HEADER_OR_FOOTER_PLACEHOLDER_TYPES } from '../constants/shape-constants';
import { BaseShapeContext } from '../context/base-shape-context';
import { DataType } from '../data';
import { DataModel } from './data-model';
import type { IGroupShapeDataModel } from './group-shape-data-model';
import type { IEventManager } from '../manager/event-manager';
import type { IData, ShapeDataType } from '../data';
import type { ICTPlaceholderPml, ICTTransform2D } from '@tencent/slide-sdk-pb-model';
import type { ILayoutDataModel } from './layout-data-model';
import type { RestrictedDataModel } from './data-model';
import type { BaseShapeMutationAPI } from '../api/base-shape-mutation-api';
import type { PageContext } from '../context/page-context';
import type { IPageDataModel } from './page-data-model';
import type { ShapeTag } from '../data/ai/description-data';

export abstract class BaseShapeDataModel<
    IDataModelData extends IData<ShapeDataType> & {
        nvPr?: {
            ph?: ICTPlaceholderPml;
            name?: string;
            descr?: string;
        };
        spPr?: {
            xfrm?: ICTTransform2D;
        };
    } = IData<ShapeDataType>,
> extends DataModel<IDataModelData> {
    public declare api: BaseShapeMutationAPI<any>;
    public context: BaseShapeContext;

    public constructor(events: IEventManager, context: PageContext) {
        super(events);
        this.context = new BaseShapeContext(context);
        this.context.setTarget(this);
    }

    public getPage(): IPageDataModel {
        return this.context.getPage();
    }

    public getFrame(): Frame {
        return Frame.fromTransform(this.props.spPr?.xfrm ?? {});
    }

    /**
     * 特殊占位符是否可见
     * @param targetLayout 目标版式
     * @returns
     */
    public isSpecialPlaceholderVisible(targetLayout: ILayoutDataModel) {
        if (!this.isSpecialPlaceholder) return;
        const layout = targetLayout;
        const master = layout.getMaster();
        const isHidden =
            !master?.props.hf ||
            (layout?.isTitleLayout && this.context.getPresentation().props.showSpecialPlsOnTitleSld === false);
        if (isHidden) return;
        const key = STPlaceholderType[this.placeholderType];
        if (get(master.props.hf, key) !== false) {
            return true;
        }
    }

    /**
     * 获取该图形的所有祖先元素：获取此 shape 的爸爸、爷爷、太爷爷...
     * 不包括 spTree 节点
     */
    public getAncestorShapes(): IGroupShapeDataModel[] {
        const ancestorShapes: IGroupShapeDataModel[] = [];
        this.forEachAncestorShapes((shape) => {
            ancestorShapes.push(shape);
        });
        return ancestorShapes;
    }

    /**
     * 遍历所有的父元素
     * 默认从子元素往父元素遍历，可以使用 reverse 参数从父往子遍历
     */
    public forEachAncestorShapes(callback: (shape: IGroupShapeDataModel) => void, options: { reverse?: boolean } = {}) {
        const { reverse } = options;
        if (this.parent.parent.type === DataType.GroupShape) {
            const parent = this.parent as unknown as IGroupShapeDataModel;
            if (!reverse) {
                callback(parent);
                parent.forEachAncestorShapes(callback, { reverse });
            } else {
                parent.forEachAncestorShapes(callback, { reverse });
                callback(parent);
            }
        }
    }

    public getDescription(): (Record<string, any> & ShapeTag) | undefined {
        const { descr } = this.props.nvPr ?? {};
        if (!descr) {
            return undefined;
        }

        try {
            return JSON.parse(descr);
        } catch (e) {
            // 不合法的 descr，可能是脏数据
            return undefined;
        }
    }

    /**
     * ph 只要存在就是 placeholder，无论 idx 和 type 是否存在
     *
     * idx 不存在时，样式继承自 idx 同样不存在的占位符
     * type 不存在认为是内容占位符
     * */
    public get isPlaceholder() {
        return !!this.props.nvPr?.ph;
    }

    /**
     * 判断是否为header or footer占位符
     */
    public get isSpecialPlaceholder() {
        return this.placeholderType && HEADER_OR_FOOTER_PLACEHOLDER_TYPES.includes(this.placeholderType);
    }

    public get placeholderType() {
        return this.props.nvPr?.ph?.type || STPlaceholderType.PBDEFAULT;
    }

    public get name() {
        return this.props.nvPr?.name;
    }
}

export type IBaseShapeDataModel = RestrictedDataModel<BaseShapeDataModel>;
